/**
 * NewBusinessDashboard — the New Business Assistant's view. Vicky Bridges's
 * production seat (login bridgesvix@gmail.com); Marinus retains the role too
 * so he can continue to self-test and step in.
 *
 * The BOARD is the shared pipeline, identical to the Adviser's and the
 * Administration Assistant's — same six columns, same cards (CLAUDE.md §30).
 * Vicky sees client-file folders alongside her prep sheets so she has the
 * team's full picture, but she cannot open or act on a file: file cards are
 * rendered inert by SharedPipeline and FileDetail redirects her active role.
 * That access gating is unchanged.
 *
 * Only the TOP section is role-specific: greeting, daily motivational quote
 * (same QuoteCard + quote source as the other roles), the intro copy, the
 * month filter, and the unified statistics bar (CLAUDE.md §37) fixed to
 * its "Prep requests" set (Submitted / In Progress / Completed / Avg. Days /
 * Overdue), computed over preparation_requests only. File statistics and the bonus counter live on
 * the other dashboards and are computed from files only — the two data
 * sources never mix (stats contract, §21.1 / §27.6).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Filter, Target,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import QuoteCard from '../components/dashboard/QuoteCard';
import StatsBar from '../components/dashboard/StatsBar';
import SharedPipeline from '../components/dashboard/SharedPipeline';
import { getAllFiles } from '../api/filesApi';
import { getAllPrepRequests } from '../api/prepRequestsApi';
import { getNoteCountsByFile } from '../api/fileNotesApi';
import { getFlaggedCountsByFile } from '../api/caseScreenshotsApi';
import { getActiveReviewClients, getReviewGroups } from '../api/reviewClientsApi';
import { getNoteCountsByPrep } from '../api/prepNotesApi';
import { getAllReferrals } from '../api/referralsApi';
import { getRandomQuote } from '../data/motivationalQuotes';
import { getGreeting, getCurrentMonthYear } from '../utils/dateHelpers';
import useAutoRefresh from '../hooks/useAutoRefresh';
import DashboardReturnToast, { useDashboardReturn } from '../components/shared/DashboardReturnToast';

const EMAIL_TO_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
  'bridgesvix@gmail.com': 'Vicky',
};

function getFirstName(email) {
  return EMAIL_TO_NAME[(email || '').toLowerCase()] || 'there';
}

export default function NewBusinessDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Client files — DISPLAY ONLY on this board: Vicky sees them for context
  // but cannot open or act on them (gated in SharedPipeline + FileDetail).
  // They feed no statistic here; the statistics bar below is prep-only.
  const [files, setFiles] = useState([]);
  const [noteCounts, setNoteCounts] = useState({});
  const [pendingNoteCountsByRole, setPendingNoteCountsByRole] = useState({});
  const [flaggedCounts, setFlaggedCounts] = useState({});
  const [prepRequests, setPrepRequests] = useState([]);
  // Prep-request note counts (total + role-keyed pending) — drives prep-card
  // badges + owner-only acknowledgement illumination (CLAUDE.md §31).
  const [prepNoteCounts, setPrepNoteCounts] = useState({});
  const [prepPendingNoteCountsByRole, setPrepPendingNoteCountsByRole] = useState({});
  // Review calendar cards (§34.9/§34.10) — display-only on this board.
  const [reviewClients, setReviewClients] = useState([]);
  const [reviewGroupLabels, setReviewGroupLabels] = useState({});
  // Referral cards — display only on this board (Adviser-only to open).
  // Never fed to prep stats or any other computation.
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(getCurrentMonthYear());
  const [quote] = useState(() => getRandomQuote());

  // `silent: true` re-runs the exact same queries WITHOUT toggling the
  // loading state — used by the background auto-refresh so data updates
  // in place with no visual interruption.
  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [data, preps, noteData, flags, prepNoteData, reviews, reviewGroups, referralRows] = await Promise.all([
        getAllFiles().catch(() => []),
        getAllPrepRequests().catch(() => []),
        getNoteCountsByFile().catch(() => ({})),
        getFlaggedCountsByFile().catch(() => ({})),
        getNoteCountsByPrep().catch(() => ({})),
        // Review calendar cards (§34.9/§34.10) — DISPLAY ONLY on this
        // board: visible to every role, openable by the Adviser alone.
        // Never fed to prep stats or any other computation.
        getActiveReviewClients().catch(() => []),
        getReviewGroups().catch(() => ({})),
        // Referral cards — DISPLAY ONLY on this board: visible to every
        // role, openable by the Adviser alone (a later step wires the
        // detail view). Never fed to stats, the bonus counter, SLA or
        // concurrency. A missing table degrades to no referral cards.
        getAllReferrals().catch(() => []),
      ]);
      const { counts = {}, pendingByRole = {} } = noteData || {};
      const { counts: prepCounts = {}, pendingByRole: prepPendingByRole = {} } = prepNoteData || {};
      setFiles(data);
      setNoteCounts(counts);
      setPendingNoteCountsByRole(pendingByRole);
      setFlaggedCounts(flags);
      setPrepRequests(preps);
      setPrepNoteCounts(prepCounts);
      setPrepPendingNoteCountsByRole(prepPendingByRole);
      setReviewClients(reviews);
      setReviewGroupLabels(reviewGroups);
      setReferrals(referralRows);
    } catch (err) {
      console.error('Failed to load the pipeline:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Return-to-dashboard (CLAUDE.md §39): toast + card highlight carried in
  // location.state by src/utils/returnToDashboard.js.
  const { toast: returnToast } = useDashboardReturn({ ready: !loading });

  // Silent auto-refresh: refetch when the tab regains visibility, plus
  // background polling every 45s while visible. Reuses load in silent
  // mode — state updates in place, no loading flash, no scroll reset.
  useAutoRefresh(() => load({ silent: true }));

  // Refresh note counts after the login catch-up gate (§31.9) acknowledges
  // notes, so cards stop flashing without a manual reload.
  useEffect(() => {
    const onAck = () => load();
    window.addEventListener('inbox:notes-acknowledged', onAck);
    return () => window.removeEventListener('inbox:notes-acknowledged', onAck);
  }, [load]);

  return (
    <div>
      {/* Return-to-dashboard toast (§39) */}
      <DashboardReturnToast message={returnToast} />

      {/* Greeting + daily motivational quote — same landing-page pattern
          (and the same QuoteCard / quote source) as the other roles. */}
      <div style={styles.greeting}>
        <h2 style={styles.greetingText}>{getGreeting(getFirstName(user?.email))}</h2>
      </div>
      <div style={styles.quoteWrap}>
        <QuoteCard quote={quote} />
      </div>
      <p style={styles.intro}>
        Here are your preparation requests — open one to start it, tick off its
        tasks and mark it complete before the deadline.
      </p>

      {/* Month filter — drives the Completed stat + column rollover. */}
      <div style={styles.filterRow}>
        <Filter size={16} color="#6B7A8D" />
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={styles.monthInput}
        />
      </div>

      {/* Unified statistics bar (CLAUDE.md §37) — fixed "Prep requests"
          set for the New Business Assistant, computed from
          preparation_requests only (§27.6). */}
      <StatsBar prepRequests={prepRequests} filterMonth={filterMonth} />

      <h3 style={styles.sectionTitle}>
        <Target size={16} /> Pipeline Overview
      </h3>

      {loading ? (
        <motion.div style={styles.loading} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          Loading pipeline…
        </motion.div>
      ) : (
        <SharedPipeline
          files={files}
          prepRequests={prepRequests}
          reviewClients={reviewClients}
          reviewGroupLabels={reviewGroupLabels}
          referrals={referrals}
          noteCounts={noteCounts}
          pendingNoteCountsByRole={pendingNoteCountsByRole}
          prepNoteCounts={prepNoteCounts}
          prepPendingNoteCountsByRole={prepPendingNoteCountsByRole}
          flaggedCounts={flaggedCounts}
          filterMonth={filterMonth}
          onPrepClick={(p) => navigate(`/prep/${p.id}`)}
        />
      )}
    </div>
  );
}

const styles = {
  greeting: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  greetingText: {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
  },
  quoteWrap: {
    maxWidth: '500px',
    marginBottom: '1rem',
  },
  intro: {
    fontSize: '0.9rem',
    color: '#6B7A8D',
    lineHeight: 1.55,
    maxWidth: '640px',
    marginBottom: '1.25rem',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  monthInput: {
    padding: '0.35rem 0.6rem',
    border: '1px solid #D0DAE6',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    width: 'auto',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.75rem',
  },
  loading: {
    color: '#6B7A8D',
    padding: '2rem 0',
    fontStyle: 'italic',
  },
};
